/**
 * Blog Post 24: Shorter or Longer Mortgage Term in Hong Kong?
 * 按揭年期揀短定長？供款與總利息的取捨
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

const cellSx = { border: '1px solid #dbe4e2', p: 1, textAlign: 'right' as const };
const headSx = { ...cellSx, bgcolor: '#f1f5f4', fontWeight: 700 };

export default function BlogPost24() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';
    const slug = '/blog/shorter-vs-longer-mortgage-term-hong-kong';

    useEffect(() => {
        trackPageView(slug, 'Blog Post 24');
        document.title = isEnglish
            ? 'Shorter or Longer Mortgage Term in Hong Kong? Payment vs Total Interest | PayWise365'
            : '按揭年期揀短定長？月供與總利息的取捨 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'Shorter or Longer Mortgage Term in Hong Kong? Monthly Payment vs Total Interest'
        : '按揭年期揀短定長？月供與總利息的取捨';
    const seoDescription = isEnglish
        ? 'Compare 15, 20, 25 and 30-year terms on a HK$2 million mortgage. See how term length changes the monthly payment and total interest, and how to choose.'
        : '以港幣200萬元按揭比較15、20、25及30年期，了解年期如何影響月供與總利息，以及如何作出選擇。';

    const rows = [
        { term: isEnglish ? '15 years' : '15年', payment: '14,298', interest: '0.57M' },
        { term: isEnglish ? '20 years' : '20年', payment: '11,599', interest: '0.78M' },
        { term: isEnglish ? '25 years' : '25年', payment: '10,012', interest: '1.00M' },
        { term: isEnglish ? '30 years' : '30年', payment: '8,981', interest: '1.23M' },
    ];

    const table = (
        <Box sx={{ overflowX: 'auto', my: 2 }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 420 }}>
                <thead>
                    <tr>
                        <Box component="th" sx={headSx}>{isEnglish ? 'Term' : '年期'}</Box>
                        <Box component="th" sx={headSx}>{isEnglish ? 'Monthly payment (HK$)' : '每月供款（港幣）'}</Box>
                        <Box component="th" sx={headSx}>{isEnglish ? 'Total interest (HK$)' : '總利息（港幣）'}</Box>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.term}>
                            <Box component="td" sx={cellSx}>{row.term}</Box>
                            <Box component="td" sx={cellSx}>{row.payment}</Box>
                            <Box component="td" sx={cellSx}>{row.interest}</Box>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={isEnglish
                    ? 'mortgage term Hong Kong, 20 year vs 30 year mortgage, shorter mortgage term, total mortgage interest, monthly mortgage payment, 按揭年期'
                    : '按揭年期, 20年按揭, 30年按揭, 總利息, 月供比較, 香港按揭'}
                canonicalUrl={`https://www.paywise365.com${slug}`}
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-10-09T00:00:00+08:00"
                modifiedDate="2026-10-09T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Mortgage Guide' : '按揭指南'}
                tags={isEnglish
                    ? ['Mortgage', 'Loan Term', 'Total Interest', 'Hong Kong Home Loan']
                    : ['按揭', '還款年期', '總利息', '香港置業']}
            />
            <Box sx={{ bgcolor: '#155e63', color: 'white', py: { xs: 5, md: 7 } }}>
                <Container maxWidth="md">
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Home' : '首頁'}</a>
                        {' / '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Blog' : '資訊'}</a>
                        {' / '}
                        {isEnglish ? 'Mortgage Guide' : '按揭指南'}
                    </Typography>
                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'Shorter or Longer Mortgage Term?'
                            : '按揭年期揀短定長？'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.92 }}>
                        {isEnglish
                            ? 'A lower monthly payment and a lower total cost pull in opposite directions. Here is how to weigh them.'
                            : '月供較低與總成本較低往往此消彼長，以下說明如何取捨。'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box component="article" sx={{ color: '#334155', lineHeight: 1.85, '& h2': { mt: 4, mb: 1.5, color: '#123b40', fontWeight: 700 }, '& p': { mb: 2 } }}>
                    {isEnglish ? (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                Mortgage Guide · Published 9 October 2026 · 5 min read
                            </Typography>
                            <Typography>
                                When you take out a mortgage, the repayment term is one of the few levers you control. A longer term makes each payment smaller and easier to qualify for, but you pay interest for more years. A shorter term does the opposite. This article compares common terms side by side so you can see the trade-off in numbers.
                            </Typography>

                            <Typography variant="h5">Same loan, four different terms</Typography>
                            <Typography>
                                The table below uses a HK$2 million loan at a constant 3.5% a year with equal monthly payments. Real mortgages often use floating rates, so treat these as illustrations rather than bank quotations.
                            </Typography>
                            {table}
                            <Typography variant="body2" color="text.secondary">
                                Figures are rounded and exclude fees and insurance. Total interest is shown in HK$ millions.
                            </Typography>
                            <Typography>
                                Moving from 30 years to 20 years raises the monthly payment by about HK$2,600, but cuts total interest by roughly HK$450,000. The longer you stretch the term, the more each extra year costs you in interest, because the balance falls more slowly.
                            </Typography>

                            <Typography variant="h5">When a longer term can make sense</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>Qualification:</strong> A lower payment can help you meet the lender’s income and debt-servicing requirements, subject to the bank’s own rules and any regulatory limits on maximum term.</li>
                                    <li><strong>Cash-flow safety:</strong> A smaller required payment leaves room for an emergency fund and other goals, which matters if your income is variable.</li>
                                    <li><strong>Flexibility:</strong> You can often choose a longer term and still pay extra when you can. Check whether your lender charges fees for partial repayment.</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">When a shorter term can make sense</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>Lower total interest:</strong> If the higher payment fits comfortably, you keep more of your money rather than paying it to the lender.</li>
                                    <li><strong>Faster equity:</strong> Principal falls sooner, so you own a larger share of the property earlier.</li>
                                    <li><strong>Debt-free sooner:</strong> This can matter if you plan to retire or reduce work within the term.</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">A middle path: long term, extra payments</Typography>
                            <Typography>
                                Some borrowers choose a longer term for a safer required payment, then voluntarily pay more when cash allows. This captures part of the interest saving without locking in a payment that could strain the budget. It only works if you actually make the extra payments and your lender allows them without costly fees, so confirm the terms first.
                            </Typography>

                            <Typography variant="h5">Check your own numbers</Typography>
                            <Typography>
                                Enter your loan amount, rate and term in the <a href="/calculators/house-loan" style={{ color: '#0f766e' }}>house loan calculator</a> and compare a few terms. To test paying extra, use the <a href="/calculators/partial-repayment" style={{ color: '#0f766e' }}>partial repayment calculator</a>. For background, read how <a href="/blog/hong-kong-mortgage-amortization-explained" style={{ color: '#0f766e' }}>mortgage amortization</a> works and <a href="/blog/how-much-can-you-borrow-hk10000-monthly-mortgage" style={{ color: '#0f766e' }}>how much a monthly payment can borrow</a>.
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                This article is for general educational purposes and is not personal financial or mortgage advice. Rates, fees, maximum terms and loan conditions vary by lender and can change.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                按揭指南 · 發佈日期：2026年10月9日 · 閱讀時間：5分鐘
                            </Typography>
                            <Typography>
                                申請按揭時，還款年期是少數由你自行決定的條件。年期越長，每月供款越低、也較易通過審批，但需要多付數年利息；年期越短則相反。本文以數字並排比較常見年期，方便你看清當中的取捨。
                            </Typography>

                            <Typography variant="h5">同一筆貸款，四種年期</Typography>
                            <Typography>
                                下表以港幣200萬元貸款、全期年利率維持3.5%、每月等額供款計算。實際按揭常採用浮動利率，因此以下只作示例，並非銀行報價。
                            </Typography>
                            {table}
                            <Typography variant="body2" color="text.secondary">
                                數字已作四捨五入，未計及費用及保險。總利息以港幣百萬元計。
                            </Typography>
                            <Typography>
                                年期由30年縮短至20年，每月供款增加約港幣2,600元，但總利息可減少約港幣45萬元。年期拉得越長，多一年所多付的利息越多，因為結餘下降得更慢。
                            </Typography>

                            <Typography variant="h5">較長年期的適用情況</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>通過審批：</strong>較低供款有助符合銀行對收入及負債供款的要求，但仍須視乎銀行規定及監管機構對最長年期的限制。</li>
                                    <li><strong>現金流較穩：</strong>必須供款較低，可預留空間給緊急備用金及其他目標，對收入不穩定的人尤其重要。</li>
                                    <li><strong>具彈性：</strong>可先選較長年期，有餘錢時再額外還款，但請先確認銀行是否就部分提早還款收取費用。</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">較短年期的適用情況</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>總利息較低：</strong>若較高供款仍能輕鬆負擔，就可少付利息給銀行。</li>
                                    <li><strong>更快累積業權：</strong>本金下降較快，你更早擁有物業較大比例的權益。</li>
                                    <li><strong>更早還清債務：</strong>如打算在還款期內退休或減少工作，這一點尤其重要。</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">折衷做法：長年期加額外還款</Typography>
                            <Typography>
                                有些借款人會選較長年期，令必須供款較穩妥，再於資金充裕時自願多還。這可享有部分利息節省，又不必鎖定一個可能令預算吃緊的供款。前提是你真的會額外還款，而且銀行容許並不收取高昂費用，請先確認條款。
                            </Typography>

                            <Typography variant="h5">代入自己的數字</Typography>
                            <Typography>
                                在<a href="/calculators/house-loan" style={{ color: '#0f766e' }}>房屋貸款計算機</a>輸入貸款額、利率及年期，比較不同年期的結果。想測試額外還款，可使用<a href="/calculators/partial-repayment" style={{ color: '#0f766e' }}>部分還款計算機</a>。你亦可閱讀<a href="/blog/hong-kong-mortgage-amortization-explained" style={{ color: '#0f766e' }}>按揭攤還解析</a>及<a href="/blog/how-much-can-you-borrow-hk10000-monthly-mortgage" style={{ color: '#0f766e' }}>月供可借幾多</a>了解更多。
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                本文只作一般教育用途，並非個人財務或按揭建議。利率、費用、最長年期及貸款條件因銀行而異，亦可能有所變動。
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
